export type FilePreviewKind =
  | 'image'
  | 'pdf'
  | 'video'
  | 'audio'
  | 'text'
  | 'spreadsheet'
  | 'document' // docx, odt
  | 'presentation' // pptx visual renderer
  | 'archive' // zip, tar, gzip listing
  | 'pcap'
  | 'email'
  | 'har'
  | 'certificate'
  | 'binary';

export interface FilePreviewResolution {
  kind: FilePreviewKind;
  mime: string;
}

export const TEXT_PREVIEW_LIMIT = 50_000;
export const TEXT_EDITOR_LIMIT = 256 * 1024;
export const SPREADSHEET_MAX_ROWS = 200;
export const SPREADSHEET_MAX_COLS = 40;
export const SNIFF_HEADER_BYTES = 512;
export const GZIP_DECOMPRESS_LIMIT = 32 * 1024 * 1024;

const GENERIC_BINARY = 'application/octet-stream';

// Note: extension->MIME is used as a best-effort fallback for preview & labels.
// For preview, we still sniff magic bytes when possible.
const EXTENSION_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
  avif: 'image/avif',
  tif: 'image/tiff',
  tiff: 'image/tiff',

  pdf: 'application/pdf',

  mp4: 'video/mp4',
  webm: 'video/webm',
  ogv: 'video/ogg',
  mov: 'video/quicktime',
  m4v: 'video/mp4',

  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  aac: 'audio/aac',
  m4a: 'audio/mp4',
  flac: 'audio/flac',
  opus: 'audio/opus',

  txt: 'text/plain',
  json: 'application/json',
  xml: 'application/xml',
  md: 'text/markdown',
  log: 'text/plain',
  yaml: 'text/yaml',
  yml: 'text/yaml',
  html: 'text/html',
  htm: 'text/html',
  css: 'text/css',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',

  js: 'text/javascript',
  ts: 'text/typescript',
  py: 'text/x-python',
  ps1: 'text/x-powershell',
  sh: 'text/x-shellscript',
  bash: 'text/x-shellscript',
  sql: 'application/sql',
  ini: 'text/plain',
  toml: 'text/plain',
  conf: 'text/plain',

  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',

  // Office (only the OOXML / ODT variants get a JS renderer)
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  odt: 'application/vnd.oasis.opendocument.text',

  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ppt: 'application/vnd.ms-powerpoint',

  zip: 'application/zip',
  tar: 'application/x-tar',
  'tar.gz': 'application/gzip',
  gz: 'application/gzip',
  tgz: 'application/gzip',

  eml: 'message/rfc822',
  msg: 'application/vnd.ms-outlook',
  har: 'application/json',

  pem: 'application/x-pem-file',
  crt: 'application/x-x509-ca-cert',
  cer: 'application/pkix-cert',
  csr: 'application/pkcs10',

  pcap: 'application/vnd.tcpdump.pcap',
  cap: 'application/vnd.tcpdump.pcap',
  pcapng: 'application/x-pcapng',
};

const SPREADSHEET_MIMES = new Set([
  'text/csv',
  'application/csv',
  'text/tab-separated-values',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
]);

const TEXT_MIMES = new Set([
  'application/json',
  'application/xml',
  'text/xml',
  'application/javascript',
  'application/xhtml+xml',
  'application/sql',
]);

const PDF_MIME = 'application/pdf';

const ZIP_MIMES = new Set([
  'application/zip',
  'application/x-zip-compressed',
]);

const TAR_MIMES = new Set([
  'application/x-tar',
  'application/tar',
]);

const GZIP_MIMES = new Set([
  'application/gzip',
  'application/x-gzip',
]);

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const ODT_MIME = 'application/vnd.oasis.opendocument.text';
const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

const EMAIL_MIMES = new Set([
  'message/rfc822',
  'application/eml',
  'application/vnd.ms-outlook',
]);

const CERT_MIMES = new Set([
  'application/x-pem-file',
  'application/x-x509-ca-cert',
  'application/pkix-cert',
  'application/pkcs10',
  'application/x-pem-certificate',
]);

const PCAP_MIMES = new Set([
  'application/vnd.tcpdump.pcap',
  'application/pcap',
  'application/x-pcap',
  'application/pcapng',
  'application/x-pcapng',
]);

const PCAPNG_MIMES = new Set([
  'application/pcapng',
  'application/x-pcapng',
]);

// DOC/PPT stay as download-only for now.
const LEGACY_WORD_MIMES = new Set(['application/msword']);
const LEGACY_PPT_MIMES = new Set(['application/vnd.ms-powerpoint']);

const VIDEO_MIMES = new Set<string>([
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/quicktime',
]);

const AUDIO_MIMES = new Set<string>([
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'audio/aac',
  'audio/mp4',
  'audio/flac',
  'audio/opus',
]);

export function inferContentTypeFromFileName(fileName: string): string | null {
  const ext = extensionOf(fileName);
  if (!ext) return null;
  return EXTENSION_MIME[ext] ?? null;
}

function normalizeMime(value: string | null | undefined): string | null {
  const mime = value?.split(';')[0]?.trim().toLowerCase() ?? '';
  if (!mime || mime === GENERIC_BINARY) return null;
  return mime;
}

export function resolveFilePreview(
  fileName: string,
  contentType: string | null | undefined,
  header: Uint8Array = new Uint8Array(),
): FilePreviewResolution {
  const fromType = kindFromMime(normalizeMime(contentType));
  if (fromType) return applyExtensionOverride(fileName, fromType);

  const fromName = inferContentTypeFromFileName(fileName);
  const fromExt = kindFromMime(fromName);
  if (fromExt) return applyExtensionOverride(fileName, fromExt);

  const sniffed = sniffMagic(header, fileName);
  if (sniffed) return applyExtensionOverride(fileName, sniffed);

  return { kind: 'binary', mime: fromName || GENERIC_BINARY };
}

function applyExtensionOverride(fileName: string, resolution: FilePreviewResolution): FilePreviewResolution {
  const ext = extensionOf(fileName);
  if (!ext) return resolution;

  if (ext === 'har' && (resolution.kind === 'text' || resolution.mime === 'application/json')) {
    return { kind: 'har', mime: 'application/json' };
  }
  if (ext === 'eml') {
    return { kind: 'email', mime: 'message/rfc822' };
  }
  if (ext === 'msg') {
    return { kind: 'email', mime: 'application/vnd.ms-outlook' };
  }
  if (['pem', 'crt', 'cer', 'csr'].includes(ext)) {
    return { kind: 'certificate', mime: EXTENSION_MIME[ext] ?? 'application/x-pem-file' };
  }

  return resolution;
}

function kindFromMime(mime: string | null): FilePreviewResolution | null {
  if (!mime) return null;

  if (ZIP_MIMES.has(mime)) return { kind: 'archive', mime };
  if (TAR_MIMES.has(mime)) return { kind: 'archive', mime };
  if (GZIP_MIMES.has(mime)) return { kind: 'archive', mime };
  if (mime === DOCX_MIME || mime === ODT_MIME) return { kind: 'document', mime };
  if (mime === PPTX_MIME) return { kind: 'presentation', mime };
  if (PCAP_MIMES.has(mime)) return { kind: 'pcap', mime };
  if (EMAIL_MIMES.has(mime)) return { kind: 'email', mime };
  if (CERT_MIMES.has(mime)) return { kind: 'certificate', mime };

  if (LEGACY_WORD_MIMES.has(mime)) return { kind: 'binary', mime };
  if (LEGACY_PPT_MIMES.has(mime)) return { kind: 'binary', mime };

  if (mime.startsWith('image/')) return { kind: 'image', mime };
  if (mime === PDF_MIME) return { kind: 'pdf', mime };

  if (VIDEO_MIMES.has(mime) || mime.startsWith('video/')) return { kind: 'video', mime };
  if (AUDIO_MIMES.has(mime) || mime.startsWith('audio/')) return { kind: 'audio', mime };

  if (SPREADSHEET_MIMES.has(mime)) return { kind: 'spreadsheet', mime };

  if (
    mime.startsWith('text/') ||
    TEXT_MIMES.has(mime) ||
    mime.endsWith('+json') ||
    mime.endsWith('+xml') ||
    mime.endsWith('/json')
  ) {
    return { kind: 'text', mime };
  }

  return null;
}

export function extensionOf(fileName: string): string {
  const base = fileName.split(/[/\\]/).pop() ?? fileName;
  const lower = base.toLowerCase();
  if (lower.endsWith('.tar.gz') || lower.endsWith('.tgz')) {
    return lower.endsWith('.tar.gz') ? 'tar.gz' : 'tgz';
  }
  if (lower.endsWith('.gz')) return 'gz';
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1).toLowerCase();
}

export function innerNameAfterGzip(fileName: string): string {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.tar.gz')) return fileName.slice(0, -3);
  if (lower.endsWith('.tgz')) return `${fileName.slice(0, -4)}.tar`;
  if (lower.endsWith('.gz')) return fileName.slice(0, -3);
  return fileName;
}

function sniffMagic(header: Uint8Array, fileName: string): FilePreviewResolution | null {
  if (header.length < 3) return null;

  // PEM certificate / key
  if (header.length >= 11 && ascii(header, 0, 11) === '-----BEGIN') {
    const ext = extensionOf(fileName);
    if (ext === 'csr') return { kind: 'certificate', mime: 'application/pkcs10' };
    return { kind: 'certificate', mime: 'application/x-pem-file' };
  }

  // gzip
  if (header[0] === 0x1f && header[1] === 0x8b) {
    return { kind: 'archive', mime: 'application/gzip' };
  }

  // JPEG
  if (header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff) {
    return { kind: 'image', mime: 'image/jpeg' };
  }

  // PNG
  if (header.length >= 4 && header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4e && header[3] === 0x47) {
    return { kind: 'image', mime: 'image/png' };
  }

  // GIF
  if (header[0] === 0x47 && header[1] === 0x49 && header[2] === 0x46) {
    return { kind: 'image', mime: 'image/gif' };
  }

  // BMP
  if (header[0] === 0x42 && header[1] === 0x4d) {
    return { kind: 'image', mime: 'image/bmp' };
  }

  // WEBP
  if (header.length >= 12 && ascii(header, 0, 4) === 'RIFF' && ascii(header, 8, 4) === 'WEBP') {
    return { kind: 'image', mime: 'image/webp' };
  }

  // ICO
  if (header.length >= 4 && header[0] === 0x00 && header[1] === 0x00 && header[2] === 0x01 && header[3] === 0x00) {
    return { kind: 'image', mime: 'image/x-icon' };
  }

  // TIFF (little-endian: "II*\0", big-endian: "MM\0*")
  if (
    header.length >= 4
    && header[0] === 0x49
    && header[1] === 0x49
    && header[2] === 0x2a
    && header[3] === 0x00
  ) {
    return { kind: 'image', mime: 'image/tiff' };
  }
  if (
    header.length >= 4
    && header[0] === 0x4d
    && header[1] === 0x4d
    && header[2] === 0x00
    && header[3] === 0x2a
  ) {
    return { kind: 'image', mime: 'image/tiff' };
  }

  // PDF
  if (header.length >= 4 && ascii(header, 0, 4) === '%PDF') {
    return { kind: 'pdf', mime: 'application/pdf' };
  }

  // MP4: checks "ftyp" at offset 4 (cheap signature)
  if (header.length >= 8 && header[4] === 0x66 && header[5] === 0x74 && header[6] === 0x79 && header[7] === 0x70) {
    return { kind: 'video', mime: 'video/mp4' };
  }

  // WebM: 1A45DFA3
  if (header.length >= 4 && header[0] === 0x1a && header[1] === 0x45 && header[2] === 0xdf && header[3] === 0xa3) {
    return { kind: 'video', mime: 'video/webm' };
  }

  // MP3: "ID3"
  if (header[0] === 0x49 && header[1] === 0x44 && header[2] === 0x33) {
    return { kind: 'audio', mime: 'audio/mpeg' };
  }

  // WAV: "RIFF" + "WAVE"
  if (header.length >= 12 && ascii(header, 0, 4) === 'RIFF' && ascii(header, 8, 4) === 'WAVE') {
    return { kind: 'audio', mime: 'audio/wav' };
  }

  // ZIP-family magic: PK..
  if (header.length >= 4 && header[0] === 0x50 && header[1] === 0x4b && (header[2] === 0x03 || header[2] === 0x05 || header[2] === 0x07)) {
    return { kind: 'archive', mime: 'application/zip' };
  }

  // POSIX tar (ustar at offset 257)
  if (header.length >= 265 && ascii(header, 257, 5) === 'ustar') {
    return { kind: 'archive', mime: 'application/x-tar' };
  }

  // pcapng Section Header Block
  if (header.length >= 4 && header[0] === 0x0a && header[1] === 0x0d && header[2] === 0x0d && header[3] === 0x0a) {
    return { kind: 'pcap', mime: 'application/x-pcapng' };
  }

  // Classic pcap (microsecond and nanosecond, both endians)
  if (header.length >= 4) {
    const b0 = header[0];
    const b1 = header[1];
    const b2 = header[2];
    const b3 = header[3];
    const leMicro = b0 === 0xd4 && b1 === 0xc3 && b2 === 0xb2 && b3 === 0xa1;
    const beMicro = b0 === 0xa1 && b1 === 0xb2 && b2 === 0xc3 && b3 === 0xd4;
    const leNano = b0 === 0x4d && b1 === 0x3c && b2 === 0xb2 && b3 === 0xa1;
    const beNano = b0 === 0xa1 && b1 === 0xb2 && b2 === 0x3c && b3 === 0x4d;
    if (leMicro || beMicro || leNano || beNano) {
      return { kind: 'pcap', mime: 'application/vnd.tcpdump.pcap' };
    }
  }

  return null;
}

function ascii(bytes: Uint8Array, start: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(start, start + length));
}

const KIND_FALLBACK_EXT: Record<FilePreviewKind, string> = {
  image: 'jpg',
  pdf: 'pdf',
  video: 'mp4',
  audio: 'mp3',
  text: 'txt',
  spreadsheet: 'xlsx',
  document: 'docx',
  presentation: 'pptx',
  archive: 'zip',
  pcap: 'pcap',
  email: 'eml',
  har: 'json',
  certificate: 'pem',
  binary: 'blank',
};

/**
 * Returns the file-extension key used to look up the Vivid icon SVG
 * (e.g. `'pdf'`, `'docx'`, `'jpg'`).  Falls back to a sensible
 * default per preview-kind when no recognised extension is available.
 */
export function fileTypeIcon(kind: FilePreviewKind, fileName?: string): string {
  if (fileName) {
    const ext = extensionOf(fileName);
    if (ext === 'tar.gz') return 'tar';
    if (ext && VIVID_ICONS.has(ext)) return ext;
  }
  return KIND_FALLBACK_EXT[kind] ?? 'blank';
}

const VIVID_ICONS = new Set([
  '7z','aac','ai','avi','bash','blank','bmp','cer','conf','crt','css','csv','dmg','doc','docx',
  'eml','eps','epub','exe','flac','flv','folder','gif','gz','heic','htm','html','ico',
  'ini','iso','jpeg','jpg','js','json','log','m4a','m4v','md','mkv','mov','mp3',
  'msg','mp4','mpeg','ods','odt','ogg','ogv','pcap','pdf','pem','png','ppt','pptx','ps1','psd',
  'py','rar','rtf','sh','sql','svg','tar','tif','tiff','ts','tsv','tgz','txt','wav','webm','webp',
  'wma','wmv','xls','xlsx','xml','yaml','yml','zip',
]);

export function fileTypeLabelKey(resolution: FilePreviewResolution): string {
  const { kind, mime } = resolution;

  switch (kind) {
    case 'image':
      switch (mime) {
        case 'image/jpeg':
          return 'files.type.image.jpeg';
        case 'image/png':
          return 'files.type.image.png';
        case 'image/gif':
          return 'files.type.image.gif';
        case 'image/webp':
          return 'files.type.image.webp';
        case 'image/bmp':
          return 'files.type.image.bmp';
        case 'image/tiff':
          return 'files.type.image.tiff';
        case 'image/avif':
          return 'files.type.image.avif';
        case 'image/svg+xml':
          return 'files.type.image.svg';
        case 'image/x-icon':
          return 'files.type.image.ico';
        default:
          return 'files.type.image';
      }
    case 'pdf':
      return 'files.type.pdf';
    case 'video':
      return 'files.type.video';
    case 'audio':
      return 'files.type.audio';
    case 'text':
      if (mime === 'text/markdown') return 'files.type.text.markdown';
      if (mime === 'application/json' || mime.endsWith('+json')) return 'files.type.text.json';
      if (mime === 'application/xml' || mime.endsWith('+xml')) return 'files.type.text.xml';
      if (mime === 'text/html' || mime === 'application/xhtml+xml') return 'files.type.text.html';
      if (mime === 'text/css') return 'files.type.text.css';
      if (mime === 'text/javascript') return 'files.type.text.javascript';
      if (mime === 'text/typescript') return 'files.type.text.typescript';
      if (mime === 'text/x-python') return 'files.type.text.python';
      if (mime === 'text/x-powershell') return 'files.type.text.powershell';
      if (mime === 'text/x-shellscript') return 'files.type.text.shell';
      if (mime === 'application/sql') return 'files.type.text.sql';
      return 'files.type.text';
    case 'spreadsheet':
      switch (mime) {
        case 'application/vnd.ms-excel':
          return 'files.type.spreadsheet.excel';
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          return 'files.type.spreadsheet.excel';
        case 'application/vnd.oasis.opendocument.spreadsheet':
          return 'files.type.spreadsheet.ods';
        case 'text/csv':
        case 'application/csv':
          return 'files.type.spreadsheet.csv';
        case 'text/tab-separated-values':
          return 'files.type.spreadsheet.tsv';
        default:
          return 'files.type.spreadsheet';
      }
    case 'document':
      if (mime === ODT_MIME) return 'files.type.document.odt';
      return 'files.type.document.word';
    case 'presentation':
      return 'files.type.presentation.powerpoint';
    case 'archive':
      if (GZIP_MIMES.has(mime)) return 'files.type.archive.gzip';
      if (TAR_MIMES.has(mime)) return 'files.type.archive.tar';
      return 'files.type.archive.zip';
    case 'pcap':
      return PCAPNG_MIMES.has(mime) ? 'files.type.capture.pcapng' : 'files.type.capture.pcap';
    case 'email':
      return 'files.type.email';
    case 'har':
      return 'files.type.har';
    case 'certificate':
      return 'files.type.certificate';
    case 'binary':
    default:
      return 'files.type.file';
  }
}

export function fileTypeLabelKeyFor(fileName: string, contentType: string | null | undefined): string {
  const resolved = resolveFilePreview(fileName, contentType, new Uint8Array());
  return fileTypeLabelKey(resolved);
}

export function fileTypeIconFor(fileName: string, contentType: string | null | undefined): string {
  const resolved = resolveFilePreview(fileName, contentType, new Uint8Array());
  return fileTypeIcon(resolved.kind, fileName);
}

export function canEditPlainText(
  fileName: string,
  contentType: string | null | undefined,
  sizeBytes: number,
): boolean {
  if (sizeBytes > TEXT_EDITOR_LIMIT) {
    return false;
  }

  return resolveFilePreview(fileName, contentType).kind === 'text';
}

export function ensureTextFileExtension(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    throw new Error('empty_file_name');
  }

  const base = trimmed.split(/[/\\]/).pop() ?? trimmed;
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) {
    return `${trimmed.replace(/\.+$/, '')}.txt`;
  }

  return trimmed;
}
